const PROXY_HEALTH_URL = '/api/v1/health'

/** Public Fly URL that goes through Fly Proxy (auto_start). Relative /api via nginx .internal does not. */
function publicWakeUrl(): string | undefined {
  const fromEnv = import.meta.env.VITE_BACKEND_WAKE_URL as string | undefined
  if (fromEnv && fromEnv.trim()) return fromEnv.trim()
  return undefined
}

export type WakeBackendOptions = {
  healthUrl?: string
  /** Absolute URL that hits Fly's public proxy (triggers auto_start). */
  publicWakeUrl?: string
  maxAttempts?: number
  attemptTimeoutMs?: number
  retryDelayMs?: number
  fetchFn?: typeof fetch
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function isGatewayError(status: number): boolean {
  return status === 502 || status === 503 || status === 504
}

/** Only 2xx means the backend process answered. Nginx 502/503/504 means upstream is still down. */
export function isBackendAwakeStatus(status: number): boolean {
  return status >= 200 && status < 300
}

async function fetchWithTimeout(
  url: string,
  timeoutMs: number,
  fetchFn: typeof fetch
): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetchFn(url, {
      method: 'GET',
      cache: 'no-store',
      // Public wake is cross-origin; cookies not required for /health
      mode: 'cors',
      credentials: 'omit',
      signal: controller.signal,
    })
  } finally {
    clearTimeout(timer)
  }
}

async function tryWakeUrl(
  url: string,
  attemptTimeoutMs: number,
  fetchFn: typeof fetch
): Promise<'awake' | 'starting' | 'error'> {
  try {
    const res = await fetchWithTimeout(url, attemptTimeoutMs, fetchFn)
    if (isBackendAwakeStatus(res.status)) return 'awake'
    if (isGatewayError(res.status)) return 'starting'
    // 4xx from an up process still proves the machine is running
    if (res.status >= 400 && res.status < 500) return 'awake'
    return 'starting'
  } catch {
    return 'error'
  }
}

/**
 * Polls until the Fly backend is reachable. Prefers the public wake URL so
 * Fly Proxy auto_start_machines runs; nginx → .internal cannot start a stopped machine.
 */
export async function wakeBackend(options: WakeBackendOptions = {}): Promise<boolean> {
  const {
    healthUrl = PROXY_HEALTH_URL,
    publicWakeUrl: publicUrl = publicWakeUrl(),
    maxAttempts = 30,
    attemptTimeoutMs = 15_000,
    retryDelayMs = 2_000,
    fetchFn = fetch,
  } = options

  const urls = [...new Set([publicUrl, healthUrl].filter((u): u is string => Boolean(u)))]

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    for (const url of urls) {
      const result = await tryWakeUrl(url, attemptTimeoutMs, fetchFn)
      if (result === 'awake') {
        return true
      }
    }

    if (attempt < maxAttempts) {
      await sleep(retryDelayMs)
    }
  }

  return false
}

let inFlight: Promise<boolean> | null = null

/**
 * Shared wake promise so the SPA fires a single wake sequence per page load.
 * Failed attempts clear the cache so callers can retry.
 */
export function ensureBackendAwake(options?: WakeBackendOptions): Promise<boolean> {
  if (!inFlight) {
    inFlight = wakeBackend(options).then((ok) => {
      if (!ok) {
        inFlight = null
      }
      return ok
    })
  }
  return inFlight
}

/** Clears the shared wake promise (for tests / manual retry). */
export function resetBackendWake(): void {
  inFlight = null
}
